// Notification wording and the small decisions around it, kept out of the
// components so they can be tested: a module that imports lit.js cannot load
// under `bun test`, because the d3-kit specifier only resolves through the
// browser's import map.

/** How often the unread badge asks for a fresh count while the tab is visible. */
export const UNREAD_POLL_INTERVAL_MS = 60_000;

/** The badge stops counting here; past it, a number is noise. */
export const BADGE_LIMIT = 99;

/**
 * The text on the unread badge, or '' when there should be no badge.
 * @param {number} count
 * @returns {string}
 */
export function badgeText(count) {
    if (!Number.isFinite(count) || count < 1) return '';
    return count > BADGE_LIMIT ? `${BADGE_LIMIT}+` : String(count);
}

/**
 * What a notification says, as one line.
 *
 * Handles only, like everywhere else: the API sends nothing more identifying.
 * @param {{kind: string, itemTitle?: string, changelogPostTitle?: string, pollTitle?: string, actorHandle?: string}} notification
 * @returns {string}
 */
export function notificationText(notification) {
    const title = `“${notification.itemTitle ?? ''}”`;
    const actor = notification.actorHandle;

    switch (notification.kind) {
        case 'comment_added':
            return actor ? `${actor} replied to ${title}` : `New reply on ${title}`;
        case 'item_closed':
            return actor ? `${actor} closed ${title}` : `${title} was closed`;
        case 'item_reopened':
            return actor ? `${actor} reopened ${title}` : `${title} was reopened`;
        case 'changelog_credited':
            return `“${notification.changelogPostTitle ?? ''}” credits feedback you follow`;
        case 'poll_opened':
            return `New poll: “${notification.pollTitle ?? ''}”`;
        case 'poll_results_released':
            return `Results are in for “${notification.pollTitle ?? ''}”`;
        default:
            return 'Something happened on feedback you follow';
    }
}

/**
 * Where opening a notification takes you.
 * @param {{kind: string, itemId?: string, pollId?: string}} notification
 * @returns {string}
 */
export function notificationLink(notification) {
    if (notification.kind === 'changelog_credited') return '/changelog';
    if (notification.pollId) return `/polls/${notification.pollId}`;
    if (notification.itemId) return `/items/${notification.itemId}`;
    return '/notifications';
}

/**
 * The icon beside a notification in the list.
 * @param {{kind: string}} notification
 * @returns {string}
 */
export function notificationIcon(notification) {
    switch (notification.kind) {
        case 'changelog_credited':
            return 'bi-megaphone';
        case 'poll_opened':
        case 'poll_results_released':
            return 'bi-bar-chart';
        default:
            return 'bi-chat-left-text';
    }
}

/**
 * The categories switched off, from the per-category switches the settings
 * page shows — the shape PUT /me/notification-preferences takes.
 *
 * A transactional category is never in the list: it cannot be switched off,
 * and sending it as disabled would be asking for something the API ignores.
 * @param {Array<{category: string, email: boolean, transactional: boolean}>} categories
 * @returns {string[]}
 */
export function disabledCategories(categories) {
    return categories.filter((c) => !c.transactional && !c.email).map((c) => c.category);
}

/**
 * The unsubscribe token from a URL's query string, or '' when there is none.
 * @param {string} search  e.g. window.location.search
 * @returns {string}
 */
export function unsubscribeToken(search) {
    return new URLSearchParams(search).get('token') ?? '';
}
